import React, { useState } from 'react';
import { Button, Modal } from '@mui/material';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import { startCheckout } from 'src/graphql/mutations';
import { StartCheckoutMutationVariables, StartCheckoutMutation } from 'src/graphql/elomapi';
import { API } from 'aws-amplify';
import { GraphQLQuery } from '@aws-amplify/api';
import { LoadingModal } from 'src/components';
import { UTMData } from 'src/features/store/EloStore.types';
import { useEloStore } from 'src/features/store/EloStore.hooks';
import posthog from 'posthog-js';

interface CheckoutButtonProps {
  label: string;
  productId: string | null;
  flavorSelection: string | null;
  proteinSelection: string | null;
  priceSelection: string | null;
  error: string | null;
  successUrl: string;
  eloProductSlug: string;
  setError: (value: string | null) => void;
}

const CheckoutButton = ({
  label = 'Checkout →',
  productId,
  flavorSelection,
  proteinSelection,
  priceSelection,
  // ERRORS CURRENTLY UNUSED. Button is disable if fields are not selected.
  error,
  setError,
  successUrl,
  eloProductSlug,
}: CheckoutButtonProps) => {
  /**********************************************************************
   * * CREATE META DATA OBJECT
   * --------------------------------------------------------------------
   * Some meta data may be necessary for the checkout process. This will
   * be created when the component loads and used when the checkout
   * function is called.
   * --------------------------------------------------------------------
   * * UTM DATA
   * UTM data is stored in the store and can be accessed using the
   * useEloStore hook. This data is used to track the source of the
   * user's visit to the site.
   * ********************************************************************/
  const { store } = useEloStore();
  const utmData: UTMData | null = store?.analytics?.utmData ?? null;

  // * ADDITIONAL META DATA WHEN NECESSARY
  // * CREATE META DATA OBJECT
  const metaData = {
    ...utmData,
  };

  // * UNCOMMENT BELOW TO INSPECT THE STORE
  // const { store } = React.useContext(Context);
  // console.log(store)

  // * LOADING STATE TO ENABLE AND DISABLE LOADING MODAL
  const [loading, setLoading] = useState(false);

  // * GET ANONYMOUS ID FROM POSTHOG
  const anonymousId = posthog.get_distinct_id();
  const posthogPayload = {
    anonymous_id: anonymousId ?? '',
    product_id: productId ?? '',
    product_slug: eloProductSlug ?? '',
    protein_flavor: flavorSelection ?? '',
    protein_source: proteinSelection ?? '',
  };

  /**********************************************************************
   * * HANDLE CHECKOUT
   * --------------------------------------------------------------------
   * TODO: Either make this a reusable function or use the existing one.
   * CURRENT FUNCTION: /src/features/store/EloStore.transform.ts -> toStartCheckoutMutation
   * --------------------------------------------------------------------
   * I wanted to optimize the function specifically for the V2 CheckoutForm
   * component rather than re-use an older function built for a different
   * components that use the Contentful CMS. This may not be strictly necessary
   * because the function is already abstracted to a degree, but I wanted to
   * ensure that the function was optimized for the specific use case.
   * Also, this is not a complex function, so it's not a huge deal.
   * ********************************************************************/
  const handleCheckout = async () => {
    posthog.capture('checkout started', {
      ...posthogPayload,
    });

    // * CHECK IF REQUIRED VALUES ARE MISSING
    if (!productId || !flavorSelection || !proteinSelection || !priceSelection) {
      // console.log('Missing Start Checkout Value')
      posthog.capture('checkout failed', {
        ...posthogPayload,        
      });
      return null;
    }

    // * SUCCESS URL
    const success = successUrl
      ? `https://elo.health/${successUrl}/?session_id={CHECKOUT_SESSION_ID}`
      : `https://elo.health/?session_id={CHECKOUT_SESSION_ID}`;

    // * CREATE VARIANTS ARRAY
    const variants = [`${flavorSelection}`, `${proteinSelection}`];

    // * CREATE VARIABLES OBJECT
    const variables: StartCheckoutMutationVariables = {
      input: {
        lineItems: [
          {
            productId: productId,
            priceId: priceSelection,
            quantity: 1,
            variants: variants,
          },
        ],
        // customerEmail: null, // * NOT REQUIRED
        anonymousId: anonymousId ?? null,
        referenceId: null,
        preliminaryPlanId: null,
        targeting: null,
        promotionCode: null,
        successUrl: success,
        cancelUrl: 'https://elo.health/',
        metaData: JSON.stringify(metaData),
      },
    };
    const authMode = GRAPHQL_AUTH_MODE.AWS_IAM;

    try {
      setLoading(true);

      const response = await API.graphql<GraphQLQuery<StartCheckoutMutation>>({
        query: startCheckout,
        variables,
        authMode,
      });

      // * GET THE CHECKOUT URL FROM THE GRAPHQL RESPONSE
      const checkoutUrl = response?.data?.startCheckout?.checkoutUrl ?? null;

      /**********************************************************************
       * * REDIRECT TO CHECKOUT URL
       * --------------------------------------------------------------------
       * Redirect to the checkout URL if it exists. Rememver that this is
       * GatsbyJS, so we need to check if the window object exists in order
       * to ensure no failures during build time.
       * ********************************************************************/
      if (typeof window !== 'undefined') {
        if (checkoutUrl) {
          posthog.capture('checkout payment loading', {
            ...posthogPayload,
          });
          window.location.href = checkoutUrl;
        }
      }

      // * SET LOADING FALSE TO CLOSE THE MODAL
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <>
      <LoadingModal loading={loading} title="Checking out" message="Please wait..." />

      <Button
        onClick={handleCheckout}
        fullWidth
        variant="contained"
        color="primary"
        // * DISABLE BUTTON IF REQUIRED VALUES ARE MISSING
        disabled={!productId || !flavorSelection || !proteinSelection || !priceSelection}
        sx={{
          py: ['12px', '14px'],
          px: ['50px'],
        }}
      >
        {label}
      </Button>
    </>
  );
};

export default CheckoutButton;
